import { useEffect, useMemo, useRef, useState } from 'react';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Table as TanstackTable,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DeleteMatterDialog } from './delete-matter-dialog';
import { MatterCard } from './matter-card';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteMatter } from '@/api/mutations/useDeleteMatter';
import { Matter, Case } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';
import { SearchInput } from '@/new/components/search-input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import {
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
} from '@/new/components/shadcn/table';
import { Tabs, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';
import { SortIcon } from '@/new/components/table/sort-icon';
import { TableNullField } from '@/new/components/table/table-null-field';
import { TablePagination } from '@/new/components/table/table-pagination';

const fallbackTableData: Matter[] = [];
const columnHelper = createColumnHelper<Matter>();

// Fades out the bottom of a scroll area until it's scrolled to the very bottom.
// Resets to the top whenever `pageIndex` changes (e.g. paginating).
function useScrollBottomFade(pageIndex: number) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  const updateFade = (target: HTMLElement) => {
    setIsScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
  };

  useEffect(() => {
    const target = scrollContainerRef.current;
    if (target) updateFade(target);
  });

  // Scroll back to the top when changing pages.
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [pageIndex]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => updateFade(e.target as HTMLElement);

  return {
    scrollContainerRef,
    handleScroll,
    maskStyle: { maskSize: isScrolledToBottom ? '100% 120%' : '100% 100%' },
  };
}

type MatterView = 'cards' | 'table';
const MATTERS_VIEW_STORAGE_KEY = 'matters-view';

function readMatterView(): MatterView {
  try {
    return localStorage.getItem(MATTERS_VIEW_STORAGE_KEY) === 'table' ? 'table' : 'cards';
  } catch {
    return 'cards';
  }
}

// Subsets whose name matches the current search, shown as "search matches" on a matter card.
function getMatchedSubsets(matter: Matter, searchFilter: string): Array<Pick<Case, 'id' | 'name'>> {
  if (!searchFilter || !matter.cases) return [];
  const searchLower = searchFilter.toLowerCase();
  return matter.cases.filter((c) => c.name?.toLowerCase().includes(searchLower));
}

interface MatterListProps {
  matters?: Matter[];
}

export function MatterList({ matters }: MatterListProps) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const [view, setView] = useState<MatterView>(readMatterView);
  const [globalFilter, setGlobalFilter] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem(MATTERS_VIEW_STORAGE_KEY, view);
    } catch {
      // ignore write failures (e.g. storage disabled)
    }
  }, [view]);

  const nonDraftMatters = useMemo(() => matters?.filter((matter) => !matter.isDraft) ?? fallbackTableData, [matters]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Matter',
        cell: ({ row }) => row.original.name,
        meta: { sortIcon: 'string' },
      }),
      columnHelper.accessor('code', {
        header: 'Code',
        cell: ({ row }) =>
          row.original.code ? (
            <span className="rounded-md border bg-gray-50 px-1.25 py-0.5 text-xs text-gray-600">
              {row.original.code}
            </span>
          ) : (
            <TableNullField />
          ),
        meta: { sortIcon: 'string' },
      }),
      columnHelper.accessor('creatorEmail', {
        header: 'Creator',
        cell: ({ row }) => row.original.creatorEmail ?? <TableNullField />,
        meta: { sortIcon: 'string' },
      }),
      columnHelper.accessor('casesCount', {
        header: 'Subsets',
        cell: ({ getValue }) => getValue() ?? 0,
        meta: { sortIcon: 'number' },
      }),
      columnHelper.accessor('docsCount', {
        header: 'Documents',
        cell: ({ getValue }) => getValue() ?? 0,
        meta: { sortIcon: 'number' },
      }),
      columnHelper.accessor('createdDate', {
        header: 'Created',
        cell: ({ getValue }) =>
          new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long', timeStyle: 'short' }).format(
            new Date(getValue()),
          ),
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => <MatterRowActions matter={row.original} />,
      }),
      // Hidden column so the global filter also searches subset/case names (used by the cards view).
      columnHelper.accessor((row) => row.cases?.map((c) => c.name).join(' ') ?? '', {
        id: 'caseNames',
        enableGlobalFilter: true,
      }),
    ],
    [i18n.language],
  );

  const table = useReactTable({
    data: nonDraftMatters,
    columns,
    initialState: {
      sorting: [
        {
          id: 'createdDate',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 30,
      },
      // caseNames is search-only; never rendered as a table column.
      columnVisibility: {
        caseNames: false,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    state: {
      globalFilter,
    },
  });

  const rows = table.getRowModel().rows;

  const cardsFade = useScrollBottomFade(table.getState().pagination.pageIndex);

  const handleRowNavigate = (matterId: string) => {
    trackCustomEvent(TrackingEventsV2.NavigationClickMatterCard, { matterId });
    navigate(`/matters/${matterId}`);
  };

  return (
    <section className="-mt-1 flex grow flex-col overflow-hidden px-8 py-1">
      <div className="my-1 flex w-full items-center justify-between gap-2.5" data-tour="matters-list">
        <SearchInput
          placeholder="Search Matters by name, code, or creator"
          value={globalFilter}
          className="w-md"
          onChange={(e) => setGlobalFilter(e.target.value)}
        />

        <Tabs value={view} onValueChange={(value) => setView(value as MatterView)} className="relative mt-px">
          <TabsList variant="pill">
            <TabsTrigger value="cards" className="py-2 text-sm" data-testid="matters-view-cards">
              <Icon className="lucide--layout-grid" />
            </TabsTrigger>
            <TabsTrigger value="table" className="py-2 text-sm" data-testid="matters-view-table">
              <Icon className="lucide--rows-3" />
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {view === 'cards' ? (
        <div
          className="mt-2 grow overflow-auto mask-b-from-80%"
          ref={cardsFade.scrollContainerRef}
          onScroll={cardsFade.handleScroll}
          style={cardsFade.maskStyle}
        >
          {rows.length > 0 ? (
            <div className="my-2 grid grid-cols-[repeat(auto-fit,minmax(24rem,1fr))] gap-6">
              {rows.map((row) => (
                <MatterCard
                  key={row.original.id}
                  matter={row.original}
                  matchedCases={getMatchedSubsets(row.original, globalFilter)}
                  searchFilter={globalFilter}
                />
              ))}
            </div>
          ) : (
            <MattersEmptyState hasQuery={Boolean(globalFilter)} />
          )}
        </div>
      ) : (
        <MattersTableBody table={table} onRowNavigate={handleRowNavigate} hasQuery={Boolean(globalFilter)} />
      )}

      <TablePagination className="mb-4" table={table} plural="matters" />
    </section>
  );
}

function MattersEmptyState({ hasQuery }: { hasQuery: boolean }) {
  return (
    <Empty className="my-8">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icon className="lucide--inbox" />
        </EmptyMedia>
        <EmptyTitle>No matters found</EmptyTitle>
        <EmptyDescription>
          {hasQuery ? 'No matters match your search. Try adjusting it.' : 'There are no matters to display.'}
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

function MattersTableBody({
  table,
  onRowNavigate,
  hasQuery,
}: {
  table: TanstackTable<Matter>;
  onRowNavigate: (matterId: string) => void;
  hasQuery: boolean;
}) {
  const { scrollContainerRef, handleScroll, maskStyle } = useScrollBottomFade(table.getState().pagination.pageIndex);

  const rows = table.getRowModel().rows;

  return (
    <div
      className="mt-2 grow overflow-auto mask-b-from-80%"
      ref={scrollContainerRef}
      onScroll={handleScroll}
      style={maskStyle}
    >
      <TableComponent>
        <TableHeader className="sticky top-0 z-10 bg-white">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  <div
                    className="flex cursor-pointer items-center gap-2 select-none"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    <SortIcon column={header.column} />
                  </div>
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} className="cursor-pointer" onClick={() => onRowNavigate(row.original.id)}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </TableComponent>

      {rows.length === 0 && <MattersEmptyState hasQuery={hasQuery} />}
    </div>
  );
}

function MatterRowActions({ matter }: { matter: Matter }) {
  const { isLoading: isDeletingMatter } = useDeleteMatter();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Delete permission is resolved per-matter on the backend (matter-level overrides and
  // seat-based creator escalation applied); the URL-scoped useHasPermission hook cannot
  // resolve overrides on the list page where there is no matterId in the route.
  if (!matter.canDelete) return null;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-all duration-200 hover:bg-gray-200 hover:text-gray-600"
            disabled={isDeletingMatter}
            data-testid="matter-row-ellipsis-menu"
          >
            <Icon className="lucide--ellipsis-vertical h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => setDeleteDialogOpen(true)}
            disabled={isDeletingMatter}
            className="text-red-600 focus:bg-red-50 focus:text-red-700"
          >
            <Icon className="lucide--trash-2 h-4 w-4" />
            Delete matter
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteMatterDialog
        matterId={matter.id}
        matterName={matter.name}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      />
    </div>
  );
}
